import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/DeliveryChart.css';

type NoteMode = 'fixed' | 'period';

interface OrderItem {
  dong: string;
  ho: string;
  customer_name: string;
  menu: string;
  delivery_date: string;
  first_purchase?: boolean;  // 첫구매 여부 (보냉가방)
  cooler_bag_override?: boolean;  // 등록 후 수동 보정 여부
  delivery_note?: string;  // 현재 날짜에 노출되는 메모
  delivery_note_saved?: string;  // 저장된 원본 메모
  delivery_note_mode?: NoteMode;
  delivery_note_start_date?: string | null;
  delivery_note_end_date?: string | null;
  delivery_note_is_active?: boolean;
  delivery_excluded?: boolean;
  delivery_exclusion_reason?: string;
  delivery_excluded_by?: string;
  delivery_excluded_at?: string;
  manual_addition?: boolean;
  manual_addition_id?: number;
  manual_added_by?: string;
  manual_added_at?: string;
  manual_addition_reason?: string;
  manual_addition_quantity?: number;
  flyer?: boolean;  // 전단지 여부
}

interface NoteDraft {
  note: string;
  mode: NoteMode;
  startDate: string;
  endDate: string;
}

interface AddCustomerDraft {
  dong: string;
  ho: string;
  customerName: string;
  menu: string;
  quantity: string;
  isCoolerBag: boolean;
  note: string;
  reason: string;
}

interface MenuEditItemDraft {
  menu: string;
  quantity: string;
}

interface MenuEditDraft {
  dong: string;
  ho: string;
  customerName: string;
  items: MenuEditItemDraft[];
  reason: string;
}

interface StoredDriverInfo {
  is_admin?: boolean;
}

interface ChartData {
  id: number;
  apartment_name: string;
  apartment_address?: string;
  entrance_password?: string;
  delivery_date: string;
  menu_summary: { [key: string]: number };
  total_count: number;
  orders_by_dong: { [dong: string]: OrderItem[] };
  excluded_orders_by_dong?: { [dong: string]: OrderItem[] };
}

const DEFAULT_MENU_OPTIONS = ['한식 1인', '한식 2인', '간편식', '샐러드', '샌드위치'];

const DeliveryChart: React.FC = () => {
  const { apartmentName, deliveryDate } = useParams<{ apartmentName: string; deliveryDate: string }>();
  const navigate = useNavigate();
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedDong, setSelectedDong] = useState<string>('전체');
  const [highlightedCustomer, setHighlightedCustomer] = useState<string | null>(null);
  const [completedDeliveries, setCompletedDeliveries] = useState<Set<string>>(new Set());
  const [selectedForCompletion, setSelectedForCompletion] = useState<Set<string>>(new Set());
  const [completionTimes, setCompletionTimes] = useState<Map<string, string>>(new Map());
  const [lastCompletedKey, setLastCompletedKey] = useState<string>('');
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [showRemainingDetail, setShowRemainingDetail] = useState(false);
  
  // 자동 이동 모드: 'down'(아래층), 'up'(윗층), 'none'(이동없음)
  const [autoScrollMode, setAutoScrollMode] = useState<'down' | 'up' | 'none'>('down');
  
  // 배송 사진 관련 state
  const [orderPhotos, setOrderPhotos] = useState<Map<string, string>>(new Map()); // orderKey -> photo data URL
  const [photoRequired, setPhotoRequired] = useState<boolean>(false);
  const [photoRequiredApartments, setPhotoRequiredApartments] = useState<string[]>([]);
  const [uploadingPhoto, setUploadingPhoto] = useState<string | null>(null); // 업로드 중인 orderKey
  const [viewingPhoto, setViewingPhoto] = useState<string | null>(null); // 확대 보기 중인 photo URL
  const [completedPhotos, setCompletedPhotos] = useState<Set<string>>(new Set()); // 서버에 사진 있는 orderKey
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [photoTargetKey, setPhotoTargetKey] = useState<string | null>(null);
  const [autoCompleteKey, setAutoCompleteKey] = useState<string | null>(null); // 촬영 후 자동 완료 대기
  const [coolerBagUpdatingKey, setCoolerBagUpdatingKey] = useState<string | null>(null);
  const [noteUpdatingKey, setNoteUpdatingKey] = useState<string | null>(null);
  const [noteEditorKey, setNoteEditorKey] = useState<string | null>(null);
  const [noteDrafts, setNoteDrafts] = useState<Record<string, NoteDraft>>({});
  const [exclusionUpdatingKey, setExclusionUpdatingKey] = useState<string | null>(null);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [addCustomerSaving, setAddCustomerSaving] = useState(false);
  const [additionUpdatingId, setAdditionUpdatingId] = useState<number | null>(null);
  const [menuEditDraft, setMenuEditDraft] = useState<MenuEditDraft | null>(null);
  const [menuEditSaving, setMenuEditSaving] = useState(false);
  const [menuEditUpdatingKey, setMenuEditUpdatingKey] = useState<string | null>(null);
  const [addCustomerDraft, setAddCustomerDraft] = useState<AddCustomerDraft>({
    dong: '',
    ho: '',
    customerName: '',
    menu: DEFAULT_MENU_OPTIONS[0],
    quantity: '1',
    isCoolerBag: false,
    note: '',
    reason: '',
  });

  // 자동으로 현재 호스트의 IP를 사용 (모바일 지원)
  const getApiBase = () => {
    const explicitApiUrl =
      typeof process !== 'undefined' && process.env
        ? process.env.REACT_APP_API_URL || ''
        : '';
    if (explicitApiUrl) {
      return explicitApiUrl;
    }
    if (window.location.hostname !== 'localhost') {
      if (window.location.protocol === 'https:') {
        return window.location.origin;
      }
      return `http://${window.location.hostname}:8000`;
    }
    return 'http://localhost:8000';
  };
  
  const API_BASE = getApiBase();
  const storedDriverInfo = (() => {
    try {
      const raw = localStorage.getItem('driverInfo');
      return raw ? (JSON.parse(raw) as StoredDriverInfo) : null;
    } catch {
      return null;
    }
  })();
  const isAdmin = Boolean(storedDriverInfo?.is_admin);
  const homePath = isAdmin ? '/admin' : '/dashboard';

  const buildNoteDraft = (
    note: string = '',
    mode: NoteMode = 'fixed',
    startDate?: string | null,
    endDate?: string | null,
  ): NoteDraft => {
    const defaultDate = chartData?.delivery_date || deliveryDate || '';
    return {
      note,
      mode: mode === 'period' ? 'period' : 'fixed',
      startDate: startDate || defaultDate,
      endDate: endDate || defaultDate,
    };
  };

  useEffect(() => {
    fetchChart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apartmentName, deliveryDate]);

  // 포커스된 카드로 자동 스크롤
  useEffect(() => {
    if (!chartData || !lastCompletedKey) return;
    
    // 이동없음 모드면 스크롤하지 않음
    if (autoScrollMode === 'none') return;
    
    const allOrders = Array.from(chartData.orders_by_dong[selectedDong] || []);
    if (allOrders.length === 0) return;
    
    let nextIncompleteOrder = null;
    
    // 초기화 트리거인 경우
    if (lastCompletedKey === '_INIT_') {
      if (autoScrollMode === 'down') {
        // 아래층 모드: 첫 번째(낮은 층) 미완료 카드로
        for (const order of allOrders) {
          const orderKey = `${order.dong}-${order.ho}-${order.customer_name}`;
          if (!completedDeliveries.has(orderKey)) {
            nextIncompleteOrder = order;
            break;
          }
        }
      } else if (autoScrollMode === 'up') {
        // 윗층 모드: 마지막(높은 층) 미완료 카드로
        for (let i = allOrders.length - 1; i >= 0; i--) {
          const order = allOrders[i];
          const orderKey = `${order.dong}-${order.ho}-${order.customer_name}`;
          if (!completedDeliveries.has(orderKey)) {
            nextIncompleteOrder = order;
            break;
          }
        }
      }
    } else {
      // 마지막 완료한 카드의 인덱스 찾기
      const lastCompletedIndex = allOrders.findIndex(
        order => `${order.dong}-${order.ho}-${order.customer_name}` === lastCompletedKey
      );
      
      if (lastCompletedIndex !== -1) {
        if (autoScrollMode === 'down') {
          // 아래층 모드: 다음(아래) 미완료 카드 찾기
          for (let i = lastCompletedIndex + 1; i < allOrders.length; i++) {
            const order = allOrders[i];
            const orderKey = `${order.dong}-${order.ho}-${order.customer_name}`;
            if (!completedDeliveries.has(orderKey)) {
              nextIncompleteOrder = order;
              break;
            }
          }
          
          // 뒤에 미완료가 없으면 처음부터 찾기
          if (!nextIncompleteOrder) {
            for (let i = 0; i < lastCompletedIndex; i++) {
              const order = allOrders[i];
              const orderKey = `${order.dong}-${order.ho}-${order.customer_name}`;
              if (!completedDeliveries.has(orderKey)) {
                nextIncompleteOrder = order;
                break;
              }
            }
          }
        } else if (autoScrollMode === 'up') {
          // 윗층 모드: 이전(위) 미완료 카드 찾기
          for (let i = lastCompletedIndex - 1; i >= 0; i--) {
            const order = allOrders[i];
            const orderKey = `${order.dong}-${order.ho}-${order.customer_name}`;
            if (!completedDeliveries.has(orderKey)) {
              nextIncompleteOrder = order;
              break;
            }
          }
          
          // 앞에 미완료가 없으면 끝에서부터 찾기
          if (!nextIncompleteOrder) {
            for (let i = allOrders.length - 1; i > lastCompletedIndex; i--) {
              const order = allOrders[i];
              const orderKey = `${order.dong}-${order.ho}-${order.customer_name}`;
              if (!completedDeliveries.has(orderKey)) {
                nextIncompleteOrder = order;
                break;
              }
            }
          }
        }
      }
    }
    
    // 스크롤 실행
    if (nextIncompleteOrder) {
      const targetKey = `${nextIncompleteOrder.dong}-${nextIncompleteOrder.ho}`;
      const element = cardRefs.current.get(targetKey);
      
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }, 100);
      }
    }
  }, [lastCompletedKey, selectedDong, chartData, completedDeliveries, autoScrollMode]);

  const fetchChart = async (preferredDong?: string) => {
    if (!apartmentName || !deliveryDate) return;
    
    // 중요: 새 데이터를 가져오기 전에 이전 데이터 초기화
    // 이전 날짜/아파트 데이터가 잘못 표시되는 것을 방지
    setChartData(null);
    setSelectedDong('전체');
    setCompletedDeliveries(new Set());
    setSelectedForCompletion(new Set());
    
    setLoading(true);
    setError('');

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/api/charts/${encodeURIComponent(apartmentName)}/${deliveryDate}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('해당 날짜의 배송 차트가 없습니다');
        }
        throw new Error('차트 조회 실패');
      }

      const data = await response.json();
      setChartData(data);
      
      // 첫 동 자동 선택
      const dongs = Object.keys(data.orders_by_dong || {});
      if (dongs.length > 0) {
        const nextDong = preferredDong === '전체'
          ? '전체'
          : preferredDong && dongs.includes(preferredDong)
            ? preferredDong
            : dongs[0];
        setSelectedDong(nextDong);
      }

      // 완료된 배송 목록 가져오기
      fetchCompletedDeliveries(data.id);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedDeliveries = async (chartId: number) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await fetch(
        `${API_BASE}/api/delivery/completed/${chartId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const completedSet = new Set<string>(
          data.map((item: any) => `${item.dong}-${item.ho}-${item.customer_name}`)
        );
        setCompletedDeliveries(completedSet);
        
        // 완료 시간 정보도 저장
        const timesMap = new Map<string, string>();
        const photoSet = new Set<string>();
        data.forEach((item: any) => {
          const key = `${item.dong}-${item.ho}-${item.customer_name}`;
          timesMap.set(key, item.completed_at);
          if (item.has_photo) {
            photoSet.add(key);
          }
        });
        setCompletionTimes(timesMap);
        setCompletedPhotos(photoSet);
      }
    } catch (err) {
      console.error('완료된 배송 목록 조회 실패:', err);
    }
  };


  const cancelDeliveryComplete = async (orderKey: string) => {
    if (!chartData) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    // orderKey에서 동, 호 추출 (동-호-고객명 형식)
    const parts = orderKey.split('-');
    const dong = parts[0];
    const ho = parts[1];
    
    // 같은 동-호의 모든 주문을 찾음
    const allOrders = Object.values(chartData.orders_by_dong).flat();
    const sameHouseOrders = allOrders.filter(o => o.dong === dong && o.ho === ho);
    
    console.log('배송 완료 취소 시작:', orderKey, '같은 호수 주문 수:', sameHouseOrders.length);
    
    try {
      const canceledKeys: string[] = [];
      
      // 같은 호수의 모든 주문에 대해 취소 처리
      for (const order of sameHouseOrders) {
        const currentOrderKey = `${order.dong}-${order.ho}-${order.customer_name}`;
        
        // 완료되지 않은 주문은 건너뜀
        if (!completedDeliveries.has(currentOrderKey)) {
          continue;
        }
        
        console.log('취소 처리 중:', currentOrderKey);
        
        const response = await fetch(
          `${API_BASE}/api/delivery/complete?chart_id=${chartData.id}&dong=${order.dong}&ho=${order.ho}&customer_name=${encodeURIComponent(order.customer_name)}`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.ok) {
          canceledKeys.push(currentOrderKey);
          console.log('취소 처리 성공:', currentOrderKey);
        } else {
          const errorData = await response.json();
          console.error('취소 API 오류:', currentOrderKey, errorData);
        }
      }
      
      // 하나 이상 취소되었으면 상태 업데이트
      if (canceledKeys.length > 0) {
        setCompletedDeliveries(prev => {
          const newSet = new Set(prev);
          canceledKeys.forEach(k => newSet.delete(k));
          return newSet;
        });
        
        setCompletionTimes(prev => {
          const newMap = new Map(prev);
          canceledKeys.forEach(k => newMap.delete(k));
          return newMap;
        });
        
        console.log('배송 완료 취소 성공, 취소된 키:', canceledKeys);
      }
    } catch (err) {
      console.error('배송 완료 취소 실패:', err);
      alert('배송 완료 취소 중 오류가 발생했습니다.');
    }
  };

  const updateCoolerBagOverride = async (dong: string, ho: string, isCoolerBag: boolean) => {
    if (!chartData) return;

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const houseKey = `${dong}-${ho}`;
    setCoolerBagUpdatingKey(houseKey);

    try {
      const response = await fetch(
        `${API_BASE}/api/admin/charts/${chartData.id}/cooler-bag-override`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            dong,
            ho,
            is_cooler_bag: isCoolerBag,
          }),
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || '보냉가방 표시 저장에 실패했습니다.');
      }

      await fetchChart(selectedDong);
    } catch (err: any) {
      alert(err?.message || '보냉가방 표시 저장 중 오류가 발생했습니다.');
    } finally {
      setCoolerBagUpdatingKey(null);
    }
  };

  const clearCoolerBagOverride = async (dong: string, ho: string) => {
    if (!chartData) return;

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const houseKey = `${dong}-${ho}`;
    setCoolerBagUpdatingKey(houseKey);

    try {
      const response = await fetch(
        `${API_BASE}/api/admin/charts/${chartData.id}/cooler-bag-override?dong=${encodeURIComponent(dong)}&ho=${encodeURIComponent(ho)}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || '원본 복원에 실패했습니다.');
      }

      await fetchChart(selectedDong);
    } catch (err: any) {
      alert(err?.message || '원본 복원 중 오류가 발생했습니다.');
    } finally {
      setCoolerBagUpdatingKey(null);
    }
  };

  const openNoteEditor = (
    houseKey: string,
    currentNote: string,
    currentMode: NoteMode,
    currentStartDate?: string | null,
    currentEndDate?: string | null,
  ) => {
    setNoteDrafts(prev => ({
      ...prev,
      [houseKey]: buildNoteDraft(currentNote, currentMode, currentStartDate, currentEndDate),
    }));
    setNoteEditorKey(houseKey);
  };

  const closeNoteEditor = (houseKey: string) => {
    setNoteEditorKey(current => (current === houseKey ? null : current));
  };

  const updateNoteDraft = (
    houseKey: string,
    updater: (prevDraft: NoteDraft) => NoteDraft,
  ) => {
    setNoteDrafts(prev => ({
      ...prev,
      [houseKey]: updater(prev[houseKey] || buildNoteDraft()),
    }));
  };

  const updateHouseNote = async (dong: string, ho: string, draft: NoteDraft) => {
    if (!chartData) return;

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const houseKey = `${dong}-${ho}`;
    const trimmedNote = draft.note.trim();
    if (!trimmedNote) {
      alert('메모 내용을 입력해주세요.');
      return;
    }
    if (draft.mode === 'period') {
      if (!draft.startDate || !draft.endDate) {
        alert('기간 메모는 시작일과 종료일을 모두 입력해주세요.');
        return;
      }
      if (draft.startDate > draft.endDate) {
        alert('게시 시작일은 종료일보다 늦을 수 없습니다.');
        return;
      }
    }

    setNoteUpdatingKey(houseKey);

    try {
      const response = await fetch(
        `${API_BASE}/api/admin/charts/${chartData.id}/house-note`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            dong,
            ho,
            note: trimmedNote,
            note_type: draft.mode,
            start_date: draft.mode === 'period' ? draft.startDate : null,
            end_date: draft.mode === 'period' ? draft.endDate : null,
          }),
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || '메모 저장에 실패했습니다.');
      }

      setNoteEditorKey(null);
      await fetchChart(selectedDong);
    } catch (err: any) {
      alert(err?.message || '메모 저장 중 오류가 발생했습니다.');
    } finally {
      setNoteUpdatingKey(null);
    }
  };

  const deleteHouseNote = async (dong: string, ho: string) => {
    if (!chartData) return;

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const houseKey = `${dong}-${ho}`;
    setNoteUpdatingKey(houseKey);

    try {
      const response = await fetch(
        `${API_BASE}/api/admin/charts/${chartData.id}/house-note?dong=${encodeURIComponent(dong)}&ho=${encodeURIComponent(ho)}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || '메모 삭제에 실패했습니다.');
      }

      setNoteDrafts(prev => {
        const nextDrafts = { ...prev };
        delete nextDrafts[houseKey];
        return nextDrafts;
      });
      setNoteEditorKey(null);
      await fetchChart(selectedDong);
    } catch (err: any) {
      alert(err?.message || '메모 삭제 중 오류가 발생했습니다.');
    } finally {
      setNoteUpdatingKey(null);
    }
  };

  const removeChartCustomer = async (dong: string, ho: string, customerName: string) => {
    if (!chartData) return;

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    if (!window.confirm(`${dong}-${ho} ${customerName} 고객을 ${chartData.delivery_date} 배송 차트에서만 제거하시겠습니까?`)) {
      return;
    }

    const exclusionKey = `${dong}-${ho}-${customerName}`;
    setExclusionUpdatingKey(exclusionKey);

    try {
      const response = await fetch(
        `${API_BASE}/api/admin/charts/${chartData.id}/customer-exclusion`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            dong,
            ho,
            customer_name: customerName,
          }),
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || '고객 제거에 실패했습니다.');
      }

      setHighlightedCustomer(null);
      await fetchChart(selectedDong);
    } catch (err: any) {
      alert(err?.message || '고객 제거 중 오류가 발생했습니다.');
    } finally {
      setExclusionUpdatingKey(null);
    }
  };

  const restoreChartCustomer = async (dong: string, ho: string, customerName: string) => {
    if (!chartData) return;

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const exclusionKey = `${dong}-${ho}-${customerName}`;
    setExclusionUpdatingKey(exclusionKey);

    try {
      const response = await fetch(
        `${API_BASE}/api/admin/charts/${chartData.id}/customer-exclusion?dong=${encodeURIComponent(dong)}&ho=${encodeURIComponent(ho)}&customer_name=${encodeURIComponent(customerName)}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || '고객 복원에 실패했습니다.');
      }

      await fetchChart(selectedDong);
    } catch (err: any) {
      alert(err?.message || '고객 복원 중 오류가 발생했습니다.');
    } finally {
      setExclusionUpdatingKey(null);
    }
  };

  const openAddCustomerModal = () => {
    const defaultDong = selectedDong !== '전체' ? selectedDong : '';
    setAddCustomerDraft({
      dong: defaultDong,
      ho: '',
      customerName: '',
      menu: DEFAULT_MENU_OPTIONS[0],
      quantity: '1',
      isCoolerBag: false,
      note: '',
      reason: '',
    });
    setShowAddCustomerModal(true);
  };

  const updateAddCustomerDraft = (updates: Partial<AddCustomerDraft>) => {
    setAddCustomerDraft(prev => ({
      ...prev,
      ...updates,
    }));
  };

  const buildMenuEditItems = (orders: OrderItem[]): MenuEditItemDraft[] => {
    const counts = new Map<string, number>();
    orders.forEach(order => {
      const menu = String(order.menu || '').trim();
      if (!menu) return;
      counts.set(menu, (counts.get(menu) || 0) + 1);
    });
    const items = Array.from(counts.entries()).map(([menu, count]) => ({
      menu,
      quantity: String(count),
    }));
    return items.length > 0 ? items : [{ menu: DEFAULT_MENU_OPTIONS[0], quantity: '1' }];
  };

  const openMenuEditModal = (dong: string, ho: string, customerName: string, customerOrders: OrderItem[]) => {
    setMenuEditDraft({
      dong,
      ho,
      customerName,
      items: buildMenuEditItems(customerOrders),
      reason: '',
    });
  };

  const updateMenuEditItem = (index: number, updates: Partial<MenuEditItemDraft>) => {
    setMenuEditDraft(prev => {
      if (!prev) return prev;
      const nextItems = prev.items.map((item, itemIndex) => (
        itemIndex === index ? { ...item, ...updates } : item
      ));
      return { ...prev, items: nextItems };
    });
  };

  const addMenuEditItem = () => {
    setMenuEditDraft(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        items: [...prev.items, { menu: DEFAULT_MENU_OPTIONS[0], quantity: '1' }],
      };
    });
  };

  const removeMenuEditItem = (index: number) => {
    setMenuEditDraft(prev => {
      if (!prev) return prev;
      const nextItems = prev.items.filter((_, itemIndex) => itemIndex !== index);
      return {
        ...prev,
        items: nextItems.length > 0 ? nextItems : [{ menu: DEFAULT_MENU_OPTIONS[0], quantity: '1' }],
      };
    });
  };

  const saveMenuReplacement = async () => {
    if (!chartData || !menuEditDraft) return;

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const items = menuEditDraft.items
      .map(item => ({
        menu: item.menu.trim(),
        quantity: Number(item.quantity || 0),
      }))
      .filter(item => item.menu);

    if (items.length === 0) {
      alert('수정할 상품을 1개 이상 입력해주세요.');
      return;
    }
    if (items.some(item => !Number.isInteger(item.quantity) || item.quantity < 1 || item.quantity > 20)) {
      alert('각 상품 수량은 1~20 사이의 숫자로 입력해주세요.');
      return;
    }

    const editKey = `${menuEditDraft.dong}-${menuEditDraft.ho}-${menuEditDraft.customerName}`;
    setMenuEditSaving(true);
    setMenuEditUpdatingKey(editKey);
    try {
      const response = await fetch(
        `${API_BASE}/api/admin/charts/${chartData.id}/customer-menu-replacement`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            dong: menuEditDraft.dong,
            ho: menuEditDraft.ho,
            customer_name: menuEditDraft.customerName,
            items,
            reason: menuEditDraft.reason.trim() || null,
          }),
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || '상품 수정에 실패했습니다.');
      }

      setMenuEditDraft(null);
      await fetchChart(menuEditDraft.dong);
    } catch (err: any) {
      alert(err?.message || '상품 수정 중 오류가 발생했습니다.');
    } finally {
      setMenuEditSaving(false);
      setMenuEditUpdatingKey(null);
    }
  };

  const restoreMenuReplacement = async (dong: string, ho: string, customerName: string) => {
    if (!chartData) return;

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    if (!window.confirm(`${dong}-${ho} ${customerName} 고객의 상품 수정을 원본으로 복원하시겠습니까?`)) {
      return;
    }

    const editKey = `${dong}-${ho}-${customerName}`;
    setMenuEditUpdatingKey(editKey);
    try {
      const response = await fetch(
        `${API_BASE}/api/admin/charts/${chartData.id}/customer-menu-replacement?dong=${encodeURIComponent(dong)}&ho=${encodeURIComponent(ho)}&customer_name=${encodeURIComponent(customerName)}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || '상품 원본복원에 실패했습니다.');
      }

      await fetchChart(selectedDong);
    } catch (err: any) {
      alert(err?.message || '상품 원본복원 중 오류가 발생했습니다.');
    } finally {
      setMenuEditUpdatingKey(null);
    }
  };

  const addChartCustomer = async () => {
    if (!chartData) return;

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const dong = addCustomerDraft.dong.trim();
    const ho = addCustomerDraft.ho.trim();
    const customerName = addCustomerDraft.customerName.trim();
    const menu = addCustomerDraft.menu.trim();
    const quantity = Number(addCustomerDraft.quantity || 1);
    const note = addCustomerDraft.note.trim();
    const reason = addCustomerDraft.reason.trim();

    if (!dong || !ho || !customerName || !menu) {
      alert('동, 호, 고객명, 메뉴를 모두 입력해주세요.');
      return;
    }
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > 20) {
      alert('수량은 1~20 사이의 숫자로 입력해주세요.');
      return;
    }

    const existingOrders = Object.values(chartData.orders_by_dong).flat();
    const hasDuplicate = existingOrders.some(order =>
      order.dong === dong &&
      order.ho === ho &&
      order.customer_name === customerName &&
      order.menu === menu
    );
    if (hasDuplicate && !window.confirm('같은 동/호/고객명/메뉴가 이미 차트에 있습니다. 그래도 추가하시겠습니까?')) {
      return;
    }

    setAddCustomerSaving(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/admin/charts/${chartData.id}/customer-addition`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            dong,
            ho,
            customer_name: customerName,
            menu,
            quantity,
            is_cooler_bag: addCustomerDraft.isCoolerBag,
            note: note || null,
            reason: reason || null,
          }),
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || '고객 추가에 실패했습니다.');
      }

      setShowAddCustomerModal(false);
      await fetchChart(dong);
    } catch (err: any) {
      alert(err?.message || '고객 추가 중 오류가 발생했습니다.');
    } finally {
      setAddCustomerSaving(false);
    }
  };

  const cancelManualAddition = async (additionId: number, label: string) => {
    if (!chartData) return;

    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    if (!window.confirm(`${label} 수동 추가를 취소하시겠습니까?`)) {
      return;
    }

    setAdditionUpdatingId(additionId);
    try {
      const response = await fetch(
        `${API_BASE}/api/admin/charts/${chartData.id}/customer-addition/${additionId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || '수동 추가 취소에 실패했습니다.');
      }

      await fetchChart(selectedDong);
    } catch (err: any) {
      alert(err?.message || '수동 추가 취소 중 오류가 발생했습니다.');
    } finally {
      setAdditionUpdatingId(null);
    }
  };

  // 사진 설정 로드
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_BASE}/api/photo-settings`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        setPhotoRequired(data.photo_required || false);
        setPhotoRequiredApartments(
          (data.required_apartments || [])
            .filter((a: any) => a.photo_required)
            .map((a: any) => a.apartment_name)
        );
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 이 아파트에 사진이 필수인지 확인
  const isPhotoRequiredForApartment = useCallback(() => {
    if (!chartData) return false;
    // 아파트별 설정이 있으면 그것을 우선
    if (photoRequiredApartments.includes(chartData.apartment_name)) return true;
    // 전체 기본 설정
    return photoRequired;
  }, [chartData, photoRequired, photoRequiredApartments]);

  // 이미지 압축 함수
  const compressImage = (file: File): Promise<Blob> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      const img = new Image();
      img.onload = () => {
        // 최대 800px로 리사이즈 (배송 증거용이므로 고해상도 불필요)
        const maxSize = 800;
        let w = img.width;
        let h = img.height;
        if (w > maxSize || h > maxSize) {
          if (w > h) {
            h = Math.round(h * maxSize / w);
            w = maxSize;
          } else {
            w = Math.round(w * maxSize / h);
            h = maxSize;
          }
        }
        canvas.width = w;
        canvas.height = h;
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(
          (blob) => resolve(blob || new Blob()),
          'image/jpeg',
          0.6 // 60% 품질 (약 50~100KB 수준)
        );
      };
      img.src = URL.createObjectURL(file);
    });
  };

  // 사진 촬영 트리거
  const triggerPhotoCapture = (orderKey: string) => {
    setPhotoTargetKey(orderKey);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  // 파일 선택 핸들러
  const handlePhotoSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !photoTargetKey) return;

    const currentKey = photoTargetKey;
    
    // 압축
    const compressed = await compressImage(file);
    
    // 미리보기용 DataURL
    const reader = new FileReader();
    reader.onload = () => {
      setOrderPhotos(prev => {
        const m = new Map(prev);
        m.set(currentKey, reader.result as string);
        return m;
      });
      // 사진 저장 후 자동 완료 트리거
      setAutoCompleteKey(currentKey);
    };
    reader.readAsDataURL(compressed);
    
    setPhotoTargetKey(null);
  };

  // 촬영 후 자동 완료 처리
  useEffect(() => {
    if (autoCompleteKey && orderPhotos.has(autoCompleteKey)) {
      const key = autoCompleteKey;
      setAutoCompleteKey(null);
      // 체크박스가 선택되어있지 않으면 선택 상태로 만듦
      setSelectedForCompletion(prev => {
        const newSet = new Set(prev);
        newSet.add(key);
        return newSet;
      });
      // 약간의 딜레이 후 완료 처리 (상태 업데이트 반영 대기)
      setTimeout(() => {
        handleConfirmSingleCompletion(key);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCompleteKey, orderPhotos]);

  // 사진 삭제 (로컬 + S3)
  const deletePhoto = async (orderKey: string) => {
    if (!window.confirm('사진을 삭제하시겠습니까?')) return;
    
    // 서버에 업로드된 사진이 있으면 S3에서도 삭제
    if (completedPhotos.has(orderKey) && chartData) {
      const token = localStorage.getItem('token');
      if (token) {
        const parts = orderKey.split('-');
        const dong = parts[0];
        const ho = parts[1];
        const customerName = parts.slice(2).join('-');
        try {
          await fetch(
            `${API_BASE}/api/delivery/photo/${chartData.id}?dong=${dong}&ho=${ho}&customer_name=${encodeURIComponent(customerName)}`,
            {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${token}` }
            }
          );
        } catch (err) {
          console.error('서버 사진 삭제 실패:', err);
        }
      }
      setCompletedPhotos(prev => {
        const s = new Set(prev);
        s.delete(orderKey);
        return s;
      });
    }
    
    // 로컬에서 삭제
    setOrderPhotos(prev => {
      const m = new Map(prev);
      m.delete(orderKey);
      return m;
    });
  };

  // 사진을 S3에 업로드
  const uploadPhotoToS3 = async (orderKey: string): Promise<boolean> => {
    const photoData = orderPhotos.get(orderKey);
    if (!photoData || !chartData) return false;
    
    const token = localStorage.getItem('token');
    if (!token) return false;
    
    setUploadingPhoto(orderKey);
    
    try {
      // DataURL -> Blob
      const res = await fetch(photoData);
      const blob = await res.blob();
      
      const parts = orderKey.split('-');
      const dong = parts[0];
      const ho = parts[1];
      const customerName = parts.slice(2).join('-');
      
      const formData = new FormData();
      formData.append('chart_id', chartData.id.toString());
      formData.append('dong', dong);
      formData.append('ho', ho);
      formData.append('customer_name', customerName);
      formData.append('photo', blob, `delivery_${orderKey}.jpg`);
      
      const uploadRes = await fetch(`${API_BASE}/api/delivery/photo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (uploadRes.ok) {
        setCompletedPhotos(prev => new Set(prev).add(orderKey));
        return true;
      }
      return false;
    } catch (err) {
      console.error('사진 업로드 실패:', err);
      return false;
    } finally {
      setUploadingPhoto(null);
    }
  };

  // 사진 보기 (presigned URL)
  const viewPhoto = async (orderKey: string) => {
    // 로컬에 사진이 있으면 바로 보기
    const local = orderPhotos.get(orderKey);
    if (local) {
      setViewingPhoto(local);
      return;
    }
    
    // 서버에서 조회
    const token = localStorage.getItem('token');
    if (!token || !chartData) return;
    
    const parts = orderKey.split('-');
    const dong = parts[0];
    const ho = parts[1];
    const customerName = parts.slice(2).join('-');
    
    try {
      const res = await fetch(
        `${API_BASE}/api/delivery/photo/${chartData.id}?dong=${dong}&ho=${ho}&customer_name=${encodeURIComponent(customerName)}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.photo_url) {
        setViewingPhoto(data.photo_url);
      }
    } catch (err) {
      console.error('사진 조회 실패:', err);
    }
  };

  const handleCheckboxChange = (orderKey: string, customerKey: string) => {
    const newSelected = new Set(selectedForCompletion);
    if (newSelected.has(orderKey)) {
      newSelected.delete(orderKey);
    } else {
      newSelected.add(orderKey);
    }
    setSelectedForCompletion(newSelected);
    // 체크박스 변경은 하이라이트에 영향 없음 (여러 개 체크 가능)
  };

  const handleConfirmSingleCompletion = async (orderKey: string) => {
    if (!chartData) {
      console.error('chartData가 없습니다');
      return;
    }
    
    const token = localStorage.getItem('token');
    if (!token) return;
    
    // 사진 필수인데 사진이 없으면 차단
    if (isPhotoRequiredForApartment() && !orderPhotos.has(orderKey)) {
      alert('사진 촬영이 필요합니다. 📷 촬영 버튼을 눌러주세요.');
      return;
    }
    
    const hasPhoto = orderPhotos.has(orderKey);
    
    console.log('배송 완료 처리 시작:', orderKey);
    
    const now = new Date().toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    // orderKey에서 동, 호 추출 (동-호-고객명 형식)
    const parts = orderKey.split('-');
    const dong = parts[0];
    const ho = parts[1];
    
    // 같은 동-호의 모든 주문을 찾음 (고객명이 다르더라도 같은 집)
    const allOrders = Object.values(chartData.orders_by_dong).flat();
    const sameHouseOrders = allOrders.filter(o => o.dong === dong && o.ho === ho);
    
    console.log('같은 호수 주문 수:', sameHouseOrders.length);
    
    try {
      // 같은 호수의 모든 주문에 대해 완료 처리
      const completedKeys: string[] = [];
      
      for (const order of sameHouseOrders) {
        const currentOrderKey = `${order.dong}-${order.ho}-${order.customer_name}`;
        
        // 이미 완료된 주문은 건너뜀
        if (completedDeliveries.has(currentOrderKey)) {
          console.log('이미 완료됨, 건너뜀:', currentOrderKey);
          continue;
        }
        
        console.log('완료 처리 중:', currentOrderKey);
        
        const response = await fetch(
          `${API_BASE}/api/delivery/complete`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: new URLSearchParams({
              chart_id: chartData.id.toString(),
              dong: order.dong,
              ho: order.ho,
              customer_name: order.customer_name
            })
          }
        );

        if (response.ok) {
          completedKeys.push(currentOrderKey);
          console.log('완료 처리 성공:', currentOrderKey);
        } else {
          const errorData = await response.json();
          console.error('API 오류:', currentOrderKey, errorData);
        }
      }
      
      // 하나 이상 완료되었으면 상태 업데이트
      if (completedKeys.length > 0) {
        // 완료 후 사진이 있으면 S3에 업로드 (DeliveryStatus 레코드가 생긴 뒤 업로드해야 photo_url 저장됨)
        if (hasPhoto) {
          const uploaded = await uploadPhotoToS3(orderKey);
          if (uploaded) {
            setCompletedPhotos(prev => {
              const newSet = new Set(prev);
              completedKeys.forEach(k => newSet.add(k));
              return newSet;
            });
          } else {
            console.warn('사진 업로드 실패, 배송 완료는 처리됨');
          }
        }
        
        // 상태 업데이트 (모든 완료된 키 추가)
        setCompletedDeliveries(prev => {
          const newSet = new Set(prev);
          completedKeys.forEach(k => newSet.add(k));
          return newSet;
        });
        
        // 완료 시간 업데이트 (모든 완료된 키에 대해)
        setCompletionTimes(prev => {
          const newMap = new Map(prev);
          completedKeys.forEach(k => newMap.set(k, now));
          return newMap;
        });
        
        // 선택 해제
        const newSelected = new Set(selectedForCompletion);
        newSelected.delete(orderKey);
        setSelectedForCompletion(newSelected);
        
        // 하이라이트 해제
        setHighlightedCustomer(null);
        
        // 완료한 카드 키 저장 (useEffect 트리거)
        setLastCompletedKey(orderKey);
        
        console.log('배송 완료 처리 성공, 완료된 키:', completedKeys);
      }
    } catch (err) {
      console.error('배송 완료 처리 실패:', err);
      alert('배송 완료 처리 중 오류가 발생했습니다.');
    }
  };

  if (loading) {
    return <div className="loading">차트 불러오는 중...</div>;
  }

  if (error) {
    const getTomorrowDate = () => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split('T')[0];
    };
    
    return (
      <div className="error-container">
        <h2>차트 조회 실패</h2>
        <p className="error">{error}</p>
        <p className="error-hint">해당 날짜의 배송 차트가 없거나, 네트워크 오류일 수 있습니다.</p>
        <div className="error-actions">
          <button 
            onClick={() => {
              // URL 파라미터를 오늘(내일) 날짜로 변경하여 재조회
              const tomorrow = getTomorrowDate();
              navigate(`/chart/${apartmentName}/${tomorrow}`);
              window.location.reload();
            }} 
            className="retry-btn"
          >
            오늘 날짜로 다시 조회
          </button>
          <button onClick={() => navigate(homePath)} className="home-btn">메인화면으로</button>
        </div>
      </div>
    );
  }

  if (!chartData) {
    return <div className="no-data">데이터를 불러오지 못했습니다</div>;
  }

  const dongs = Object.keys(chartData.orders_by_dong);
  const displayOrders = selectedDong === '전체' 
    ? Object.values(chartData.orders_by_dong).flat()
    : chartData.orders_by_dong[selectedDong] || [];
  const excludedOrdersByDong = chartData.excluded_orders_by_dong || {};
  const excludedDisplayOrders = selectedDong === '전체'
    ? Object.values(excludedOrdersByDong).flat()
    : excludedOrdersByDong[selectedDong] || [];

  // 같은 동/호 그룹핑 (같은 호수는 하나의 카드로 묶음)
  // 배송에서는 동/호가 중요하고, 고객명이 다르더라도 같은 집이면 하나의 카드
  const customerGroups = new Map<string, OrderItem[]>();
  displayOrders.forEach(order => {
    const key = `${order.dong}-${order.ho}`;
    if (!customerGroups.has(key)) {
      customerGroups.set(key, []);
    }
    customerGroups.get(key)!.push(order);
  });
  const excludedCustomerGroups = new Map<string, OrderItem[]>();
  excludedDisplayOrders.forEach(order => {
    const key = `${order.dong}-${order.ho}-${order.customer_name}`;
    if (!excludedCustomerGroups.has(key)) {
      excludedCustomerGroups.set(key, []);
    }
    excludedCustomerGroups.get(key)!.push(order);
  });
  const excludedCustomerKeySet = new Set(excludedCustomerGroups.keys());

  // 보냉가방 개수 계산
  const coolerBagCount = Object.values(chartData.orders_by_dong)
    .flat()
    .filter(order => order.first_purchase)
    .length;
  
  // 전단지 개수 계산
  const flyerCount = Object.values(chartData.orders_by_dong)
    .flat()
    .filter(order => order.flyer)
    .length;

  // 동별 메뉴 합계 계산
  const dongMenuSummary = new Map<string, { [menu: string]: number }>();
  Object.entries(chartData.orders_by_dong).forEach(([dong, orders]) => {
    const summary: { [menu: string]: number } = {};
    orders.forEach(o => {
      summary[o.menu] = (summary[o.menu] || 0) + 1;
    });
    dongMenuSummary.set(dong, summary);
  });

  // 메뉴별 색상 매핑
  const getMenuColor = (menu: string): string => {
    const menuLower = menu.toLowerCase();
    if (menuLower.includes('한식 1인') || menuLower.includes('한식1인')) return 'menu-korean-1';
    if (menuLower.includes('한식 2인') || menuLower.includes('한식2인')) return 'menu-korean-2';
    if (menuLower.includes('간편식')) return 'menu-convenience';
    if (menuLower.includes('샐러드') || menuLower.includes('그린식')) return 'menu-salad';
    if (menuLower.includes('샌드위치')) return 'menu-sandwich';
    return 'menu-default';
  };

  const addCustomerMenuOptions = Array.from(new Set([
    ...DEFAULT_MENU_OPTIONS,
    ...Object.keys(chartData.menu_summary || {}),
  ]));

  return (
    <div className="delivery-chart">
      {/* 차트 헤더 */}
      <div className="chart-header">
        <div className="chart-header-actions">
          <button onClick={() => navigate(homePath)} className="back-to-main-btn">
            ← 메인화면
          </button>
          {isAdmin && (
            <button onClick={openAddCustomerModal} className="add-customer-btn">
              고객추가
            </button>
          )}
        </div>
        <h2>{chartData.apartment_name}</h2>
        {chartData.apartment_address && (
          <p className="address">{chartData.apartment_address}</p>
        )}
        {chartData.entrance_password && (
          <p className="address">공동현관: {chartData.entrance_password}</p>
        )}
        <p className="date">{chartData.delivery_date} 배송</p>
      </div>

      {/* 전체 합계 */}
      <div className="total-summary">
        <div className="summary-header">
          <h3>전체 합계: {chartData.total_count}건</h3>
          {coolerBagCount > 0 && (
            <div className="cooler-bag-summary">
              <span className="cooler-icon">❄️</span>
              <span className="cooler-text">보냉가방: <strong>{coolerBagCount}개</strong></span>
            </div>
          )}
          {flyerCount > 0 && (
            <div className="flyer-summary">
              <span className="flyer-icon">📰</span>
              <span className="flyer-text">전단지: <strong>{flyerCount}개</strong></span>
            </div>
          )}
        </div>
        <div className="menu-tags">
          {Object.entries(chartData.menu_summary)
            .sort((a, b) => {
              // 메뉴 정렬: 한식1인 → 한식2인 → 간편식 → 샐러드 → 샌드위치
              const menuA = a[0].replace(/\s+/g, '');
              const menuB = b[0].replace(/\s+/g, '');
              
              const menuOrder = ['한식1인', '한식2인', '간편식', '샐러드', '샌드위치'];
              const indexA = menuOrder.findIndex(m => menuA.includes(m));
              const indexB = menuOrder.findIndex(m => menuB.includes(m));
              
              if (indexA !== -1 && indexB !== -1) return indexA - indexB;
              if (indexA !== -1) return -1;
              if (indexB !== -1) return 1;
              return a[0].localeCompare(b[0]);
            })
            .map(([menu, count]) => (
              <span key={menu} className={`menu-tag ${getMenuColor(menu)}`}>
                {menu}: <strong>{count}</strong>
              </span>
            ))}
        </div>
      </div>

      {/* 동 선택 탭 */}
      <div className="dong-tabs">
        <button
          className={selectedDong === '전체' ? 'active' : ''}
          onClick={() => {
            setSelectedDong('전체');
            setLastCompletedKey('_INIT_'); // 초기화 트리거
          }}
        >
          전체 ({chartData.total_count})
        </button>
        {dongs.map(dong => {
          const dongOrders = chartData.orders_by_dong[dong];
          const count = dongOrders.length;
          
          // 해당 동의 모든 주문이 완료되었는지 확인
          const allCompleted = dongOrders.every(order => 
            completedDeliveries.has(`${order.dong}-${order.ho}-${order.customer_name}`)
          );
          
          return (
            <button
              key={dong}
              className={`${selectedDong === dong ? 'active' : ''} ${allCompleted ? 'completed' : ''}`}
              onClick={() => {
                setSelectedDong(dong);
                setLastCompletedKey('_INIT_'); // 초기화 트리거
              }}
            >
              {dong}동 ({count})
            </button>
          );
        })}
      </div>

      {/* 동별 메뉴 합계 */}
      {selectedDong !== '전체' && dongMenuSummary.has(selectedDong) && (() => {
        const dongSummary = dongMenuSummary.get(selectedDong)!;
        // 메뉴 정렬: 한식1인 → 한식2인 → 간편식 → 샐러드 → 샌드위치
        const sortedDongMenus = Object.entries(dongSummary).sort((a, b) => {
          const menuA = a[0];
          const menuB = b[0];
          
          // 정규화된 메뉴명 (공백 제거)
          const normalizedA = menuA.replace(/\s+/g, '');
          const normalizedB = menuB.replace(/\s+/g, '');
          
          // 한식 1인 우선 (정확한 매칭)
          const isKorean1A = normalizedA === '한식1인' || normalizedA.startsWith('한식1인');
          const isKorean1B = normalizedB === '한식1인' || normalizedB.startsWith('한식1인');
          
          if (isKorean1A && !isKorean1B) return -1;
          if (!isKorean1A && isKorean1B) return 1;
          if (isKorean1A && isKorean1B) return 0;
          
          // 한식 2인 다음 (정확한 매칭)
          const isKorean2A = normalizedA === '한식2인' || normalizedA.startsWith('한식2인');
          const isKorean2B = normalizedB === '한식2인' || normalizedB.startsWith('한식2인');
          
          if (isKorean2A && !isKorean2B) return -1;
          if (!isKorean2A && isKorean2B) return 1;
          if (isKorean2A && isKorean2B) return 0;
          
          // 나머지 메뉴 순서: 간편식, 샐러드, 샌드위치
          const menuOrder = ['간편식', '샐러드', '샌드위치'];
          const indexA = menuOrder.findIndex(m => normalizedA.includes(m));
          const indexB = menuOrder.findIndex(m => normalizedB.includes(m));
          
          if (indexA !== -1 && indexB !== -1) return indexA - indexB;
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;
          
          return menuA.localeCompare(menuB);
        });
        
        // 해당 동의 보냉가방 개수 계산
        const dongOrders = chartData.orders_by_dong[selectedDong] || [];
        const dongCoolerBagCount = dongOrders.filter(o => o.first_purchase).length;
        const dongFlyerCount = dongOrders.filter(o => o.flyer).length;
        
        return (
          <div className="dong-summary-box">
            <div className="dong-summary-header">
              <h4>{selectedDong}동 메뉴 합계</h4>
              {dongCoolerBagCount > 0 && (
                <span className="dong-cooler-badge">보냉가방 {dongCoolerBagCount}개</span>
              )}
              {dongFlyerCount > 0 && (
                <span className="dong-flyer-badge">전단지 {dongFlyerCount}개</span>
              )}
            </div>
            <div className="dong-menu-grid">
              {sortedDongMenus.map(([menu, count]) => {
                // 메뉴별 색상 클래스 결정
                const normalizedMenu = menu.replace(/\s+/g, '');
                let menuColorClass = '';
                if (normalizedMenu.includes('한식1인')) {
                  menuColorClass = 'menu-korean1';
                } else if (normalizedMenu.includes('한식2인')) {
                  menuColorClass = 'menu-korean2';
                } else if (normalizedMenu.includes('간편식')) {
                  menuColorClass = 'menu-simple';
                } else if (normalizedMenu.includes('샐러드')) {
                  menuColorClass = 'menu-salad';
                } else if (normalizedMenu.includes('샌드위치')) {
                  menuColorClass = 'menu-sandwich';
                }
                
                return (
                  <div key={menu} className={`dong-menu-item ${menuColorClass}`}>
                    <span className="dong-menu-name">{menu}</span>
                    <span className="dong-menu-count">{count}개</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* 자동 이동 옵션 */}
      <div className="auto-scroll-options">
        <span className="auto-scroll-label">자동이동:</span>
        <label className="auto-scroll-option">
          <input
            type="radio"
            name="autoScroll"
            checked={autoScrollMode === 'down'}
            onChange={() => setAutoScrollMode('down')}
          />
          <span>아래층</span>
        </label>
        <label className="auto-scroll-option">
          <input
            type="radio"
            name="autoScroll"
            checked={autoScrollMode === 'up'}
            onChange={() => setAutoScrollMode('up')}
          />
          <span>윗층</span>
        </label>
        <label className="auto-scroll-option">
          <input
            type="radio"
            name="autoScroll"
            checked={autoScrollMode === 'none'}
            onChange={() => setAutoScrollMode('none')}
          />
          <span>이동없음</span>
        </label>
      </div>

      {/* 배송 리스트 */}
      <div className="delivery-list">
        {displayOrders.length === 0 ? (
          <p className="no-orders">배송할 주문이 없습니다</p>
        ) : (
          Array.from(customerGroups.entries()).map(([key, group], index) => {
            const order = group[0]; // 첫 번째 주문 정보 사용
            const orderKey = `${order.dong}-${order.ho}-${order.customer_name}`;
            const isMultiOrder = group.length > 1;
            const hasFlyer = group.some(item => item.flyer);
            const hasCoolerBag = group.some(item => item.first_purchase);
            const hasManualCoolerBagOverride = group.some(item => item.cooler_bag_override);
            const hasManualAddition = group.some(item => item.manual_addition);
            const manualAdditions = Array.from(
              new Map(
                group
                  .filter(item => item.manual_addition && item.manual_addition_id)
                  .map(item => [item.manual_addition_id as number, item])
              ).values()
            );
            const noteSource = group.find(item => (item.delivery_note_saved?.trim() || item.delivery_note?.trim()));
            const savedDeliveryNote = noteSource?.delivery_note_saved?.trim() || noteSource?.delivery_note?.trim() || '';
            const activeDeliveryNote = noteSource?.delivery_note?.trim() || '';
            const noteMode: NoteMode = noteSource?.delivery_note_mode === 'period' ? 'period' : 'fixed';
            const noteStartDate = noteSource?.delivery_note_start_date || '';
            const noteEndDate = noteSource?.delivery_note_end_date || '';
            const hasSavedDeliveryNote = Boolean(savedDeliveryNote);
            const hasActiveDeliveryNote = Boolean(activeDeliveryNote);
            const isEditingNote = noteEditorKey === key;
            const noteDraft = noteDrafts[key] ?? buildNoteDraft(savedDeliveryNote, noteMode, noteStartDate, noteEndDate);
            const isHighlighted = highlightedCustomer === key;
            const customerNames = Array.from(new Set(
              group.map(item => item.customer_name).filter(Boolean)
            ));
            const customerOrderGroups = customerNames.map(customerName => ({
              customerName,
              orders: group.filter(item => item.customer_name === customerName),
              replacementActive: excludedCustomerKeySet.has(`${order.dong}-${order.ho}-${customerName}`) &&
                group.some(item => item.customer_name === customerName && item.manual_addition),
            }));
            const removableCustomerNames = customerNames.filter(customerName =>
              group.some(item => item.customer_name === customerName && !item.manual_addition)
            );
            
            // 모든 주문의 완료 상태 확인
            const allCompleted = group.every(o => 
              completedDeliveries.has(`${o.dong}-${o.ho}-${o.customer_name}`)
            );
            const isSelected = selectedForCompletion.has(orderKey);
            const completionTime = completionTimes.get(orderKey);
            
            return (
              <div
                key={key}
                ref={(el) => {
                  if (el) cardRefs.current.set(key, el);
                }}
                className={`order-item ${isMultiOrder ? 'multi-order' : ''} ${(isHighlighted || isSelected) ? 'highlighted' : ''} ${order.first_purchase ? 'first-purchase' : ''} ${allCompleted ? 'completed' : ''} ${(highlightedCustomer || selectedForCompletion.size > 0) && !isHighlighted && !isSelected ? 'dimmed' : ''}`}
                onClick={() => !allCompleted && setHighlightedCustomer(isHighlighted ? null : key)}
              >
                {allCompleted ? (
                  <button
                    className="undo-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      cancelDeliveryComplete(orderKey);
                    }}
                  >
                    되돌리기
                  </button>
                ) : (
                  /* 미완료 카드: 우측 상단 사진 촬영 영역 */
                  <div className="card-photo-action" onClick={(e) => e.stopPropagation()}>
                    {orderPhotos.has(orderKey) ? (
                      <>
                        <img
                          src={orderPhotos.get(orderKey)}
                          alt="배송사진"
                          className="photo-thumbnail"
                          onClick={() => setViewingPhoto(orderPhotos.get(orderKey) || null)}
                          onContextMenu={(e) => {
                            e.preventDefault();
                            deletePhoto(orderKey);
                          }}
                          onTouchStart={(e) => {
                            const timer = setTimeout(() => deletePhoto(orderKey), 600);
                            const cancel = () => clearTimeout(timer);
                            e.currentTarget.addEventListener('touchend', cancel, { once: true });
                            e.currentTarget.addEventListener('touchmove', cancel, { once: true });
                          }}
                        />
                        <button
                          className="retake-button"
                          onClick={() => triggerPhotoCapture(orderKey)}
                        >
                          🔄
                        </button>
                      </>
                    ) : uploadingPhoto === orderKey ? (
                      <span className="uploading-indicator">업로드중...</span>
                    ) : (
                      <>
                        <button
                          className="photo-capture-btn"
                          onClick={() => triggerPhotoCapture(orderKey)}
                        >
                          📷
                        </button>
                        {!isPhotoRequiredForApartment() && (
                          <button
                            className="complete-btn-small"
                            onClick={() => handleConfirmSingleCompletion(orderKey)}
                          >
                            완료
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
                
                <div className="order-location">
                  <span className="dong-ho">
                    <span className="dong-part">{order.dong}</span>
                    <span className="separator">-</span>
                    <span className="ho-part">{order.ho}</span>
                  </span>
                  {hasCoolerBag && !allCompleted && (
                    <span className="cooler-bag-badge">보냉가방</span>
                  )}
                  {hasManualCoolerBagOverride && (
                    <span className="manual-override-badge">수동</span>
                  )}
                  {hasManualAddition && (
                    <span className="manual-addition-badge">수동추가</span>
                  )}
                  {hasFlyer && (
                    <span className="flyer-badge">전단지</span>
                  )}
                  {allCompleted && (
                    <span className="completed-badge">배송완료</span>
                  )}
                </div>
                <div className="order-details">
                  <span className="customer-name">{order.customer_name}</span>
                  {isMultiOrder ? (
                    <div className="menu-list">
                      {group.map((item, idx) => (
                        <span key={idx} className={`menu ${getMenuColor(item.menu)}`}>
                          {item.menu}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className={`menu ${getMenuColor(order.menu)}`}>{order.menu}</span>
                  )}
                  {hasActiveDeliveryNote && (
                    <div className="delivery-note-banner">
                      <div className="delivery-note-header">
                        <span className="delivery-note-label">{noteMode === 'period' ? '기간메모' : '배송메모'}</span>
                        {noteMode === 'period' && noteStartDate && noteEndDate && (
                          <span className="delivery-note-period">{noteStartDate} ~ {noteEndDate}</span>
                        )}
                      </div>
                      <span className="delivery-note-text">{activeDeliveryNote}</span>
                    </div>
                  )}
                  {allCompleted && completionTime && (
                    <div className="completed-info">
                      완료: {completionTime}
                      {completedPhotos.has(orderKey) && (
                        <button
                          className="view-photo-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            viewPhoto(orderKey);
                          }}
                        >
                          🖼️
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {isAdmin && (
                  <div className="admin-order-tools" onClick={(e) => e.stopPropagation()}>
                    <div className="admin-cooler-controls">
                      <button
                        className={`admin-cooler-button ${hasCoolerBag ? 'remove' : 'add'}`}
                        onClick={() => updateCoolerBagOverride(order.dong, order.ho, !hasCoolerBag)}
                        disabled={coolerBagUpdatingKey === key}
                      >
                        {coolerBagUpdatingKey === key
                          ? '저장중...'
                          : hasCoolerBag
                            ? '보냉 해제'
                            : '보냉 표시'}
                      </button>
                      {hasManualCoolerBagOverride && (
                        <button
                          className="admin-cooler-button reset"
                          onClick={() => clearCoolerBagOverride(order.dong, order.ho)}
                          disabled={coolerBagUpdatingKey === key}
                        >
                          원본복원
                        </button>
                      )}
                    </div>
                    <div className="admin-note-controls">
                      {hasSavedDeliveryNote && (
                        <p className="admin-note-summary-text">
                          {noteMode === 'period'
                            ? `기간 ${noteStartDate} ~ ${noteEndDate}${hasActiveDeliveryNote ? '' : ' / 현재 날짜 미노출'}`
                            : '고정 메모'}
                        </p>
                      )}
                      {isEditingNote ? (
                        <div className="admin-note-editor">
                          <div className="admin-note-mode-selector">
                            <label className={`admin-note-mode-option ${noteDraft.mode === 'fixed' ? 'selected' : ''}`}>
                              <input
                                type="radio"
                                name={`note-mode-${key}`}
                                checked={noteDraft.mode === 'fixed'}
                                onChange={() => updateNoteDraft(key, prev => ({
                                  ...prev,
                                  mode: 'fixed',
                                }))}
                              />
                              <span>고정</span>
                            </label>
                            <label className={`admin-note-mode-option ${noteDraft.mode === 'period' ? 'selected' : ''}`}>
                              <input
                                type="radio"
                                name={`note-mode-${key}`}
                                checked={noteDraft.mode === 'period'}
                                onChange={() => updateNoteDraft(key, prev => ({
                                  ...prev,
                                  mode: 'period',
                                  startDate: prev.startDate || chartData.delivery_date,
                                  endDate: prev.endDate || chartData.delivery_date,
                                }))}
                              />
                              <span>기간</span>
                            </label>
                          </div>
                          {noteDraft.mode === 'period' && (
                            <div className="admin-note-date-range">
                              <label className="admin-note-date-field">
                                <span>시작일</span>
                                <input
                                  type="date"
                                  className="admin-note-date-input"
                                  value={noteDraft.startDate}
                                  onChange={(e) => updateNoteDraft(key, prev => ({
                                    ...prev,
                                    startDate: e.target.value,
                                  }))}
                                  max={noteDraft.endDate || undefined}
                                />
                              </label>
                              <label className="admin-note-date-field">
                                <span>종료일</span>
                                <input
                                  type="date"
                                  className="admin-note-date-input"
                                  value={noteDraft.endDate}
                                  onChange={(e) => updateNoteDraft(key, prev => ({
                                    ...prev,
                                    endDate: e.target.value,
                                  }))}
                                  min={noteDraft.startDate || undefined}
                                />
                              </label>
                            </div>
                          )}
                          <textarea
                            className="admin-note-textarea"
                            value={noteDraft.note}
                            onChange={(e) => updateNoteDraft(key, prev => ({
                              ...prev,
                              note: e.target.value,
                            }))}
                            placeholder={noteDraft.mode === 'period' ? '예: 이번 주만 2302-1호 로 배송' : '예: 2302-1호 로 배송'}
                            maxLength={500}
                          />
                          <div className="admin-note-actions">
                            <button
                              className="admin-note-button save"
                              onClick={() => updateHouseNote(order.dong, order.ho, noteDraft)}
                              disabled={noteUpdatingKey === key}
                            >
                              {noteUpdatingKey === key ? '저장중...' : '메모 저장'}
                            </button>
                            <button
                              className="admin-note-button cancel"
                              onClick={() => closeNoteEditor(key)}
                              disabled={noteUpdatingKey === key}
                            >
                              취소
                            </button>
                            {hasSavedDeliveryNote && (
                              <button
                                className="admin-note-button delete"
                                onClick={() => deleteHouseNote(order.dong, order.ho)}
                                disabled={noteUpdatingKey === key}
                              >
                              삭제
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <button
                          className="admin-note-button edit"
                          onClick={() => openNoteEditor(key, savedDeliveryNote, noteMode, noteStartDate, noteEndDate)}
                        >
                          {hasSavedDeliveryNote ? '메모 수정' : '메모 추가'}
                        </button>
                      )}
                    </div>
                    <div className="admin-exclusion-controls">
                      {customerOrderGroups.map(({ customerName, orders, replacementActive }) => {
                        const editKey = `${order.dong}-${order.ho}-${customerName}`;
                        return (
                          <span key={`menu-edit-${customerName}`} className="admin-menu-edit-actions">
                            <button
                              className="admin-exclusion-button edit-menu"
                              onClick={() => openMenuEditModal(order.dong, order.ho, customerName, orders)}
                              disabled={menuEditUpdatingKey === editKey}
                            >
                              {menuEditUpdatingKey === editKey ? '수정중...' : customerOrderGroups.length > 1 ? `${customerName} 상품수정` : '상품수정'}
                            </button>
                            {replacementActive && (
                              <button
                                className="admin-exclusion-button restore-menu"
                                onClick={() => restoreMenuReplacement(order.dong, order.ho, customerName)}
                                disabled={menuEditUpdatingKey === editKey}
                              >
                                원본복원
                              </button>
                            )}
                          </span>
                        );
                      })}
                      {manualAdditions.map(item => {
                        const additionId = item.manual_addition_id as number;
                        const label = `${item.dong}-${item.ho} ${item.customer_name} ${item.menu}`;
                        return (
                          <button
                            key={`addition-${additionId}`}
                            className="admin-exclusion-button cancel-addition"
                            onClick={() => cancelManualAddition(additionId, label)}
                            disabled={additionUpdatingId === additionId}
                          >
                            {additionUpdatingId === additionId ? '취소중...' : '추가 취소'}
                          </button>
                        );
                      })}
                      {removableCustomerNames.map(customerName => {
                        const exclusionKey = `${order.dong}-${order.ho}-${customerName}`;
                        return (
                          <button
                            key={customerName}
                            className="admin-exclusion-button remove"
                            onClick={() => removeChartCustomer(order.dong, order.ho, customerName)}
                            disabled={exclusionUpdatingKey === exclusionKey}
                          >
                            {exclusionUpdatingKey === exclusionKey
                              ? '제거중...'
                              : removableCustomerNames.length > 1
                                ? `${customerName} 제거`
                                : '고객 제거'}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {isMultiOrder && !allCompleted && (
                  <div className="multi-badge">
                    {group.length}개 메뉴
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {isAdmin && excludedDisplayOrders.length > 0 && (
        <div className="excluded-orders-section">
          <div className="excluded-orders-header">
            <h3>제거된 고객</h3>
            <span>{excludedDisplayOrders.length}건</span>
          </div>
          <div className="excluded-orders-list">
            {Array.from(excludedCustomerGroups.entries()).map(([excludedKey, group]) => {
              const order = group[0];
              const exclusionKey = `${order.dong}-${order.ho}-${order.customer_name}`;
              const hasVisibleReplacement = displayOrders.some(item =>
                item.dong === order.dong &&
                item.ho === order.ho &&
                item.customer_name === order.customer_name &&
                item.manual_addition
              );
              return (
                <div key={excludedKey} className="excluded-order-item">
                  <div className="excluded-order-main">
                    <strong>{order.dong}-{order.ho}</strong>
                    <span>{order.customer_name}</span>
                    <div className="excluded-menu-list">
                      {group.map((item, idx) => (
                        <span key={idx} className={`menu ${getMenuColor(item.menu)}`}>
                          {item.menu}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    className="admin-exclusion-button restore"
                    onClick={() => (
                      hasVisibleReplacement
                        ? restoreMenuReplacement(order.dong, order.ho, order.customer_name)
                        : restoreChartCustomer(order.dong, order.ho, order.customer_name)
                    )}
                    disabled={exclusionUpdatingKey === exclusionKey || menuEditUpdatingKey === exclusionKey}
                  >
                    {exclusionUpdatingKey === exclusionKey || menuEditUpdatingKey === exclusionKey ? '복원중...' : hasVisibleReplacement ? '원본복원' : '되돌리기'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isAdmin && menuEditDraft && (
        <div className="modal-overlay" onClick={() => !menuEditSaving && setMenuEditDraft(null)}>
          <div className="modal-content add-customer-modal menu-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>상품 수정</h3>
              <button
                className="close-btn"
                onClick={() => setMenuEditDraft(null)}
                disabled={menuEditSaving}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="menu-edit-target">
                <strong>{menuEditDraft.dong}-{menuEditDraft.ho}</strong>
                <span>{menuEditDraft.customerName}</span>
              </div>
              <div className="menu-edit-items">
                {menuEditDraft.items.map((item, index) => (
                  <div key={`${index}-${item.menu}`} className="menu-edit-row">
                    <label>
                      <span>상품</span>
                      <select
                        value={item.menu}
                        onChange={(e) => updateMenuEditItem(index, { menu: e.target.value })}
                        disabled={menuEditSaving}
                      >
                        {addCustomerMenuOptions.map(menu => (
                          <option key={menu} value={menu}>{menu}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>수량</span>
                      <input
                        type="number"
                        min="1"
                        max="20"
                        value={item.quantity}
                        onChange={(e) => updateMenuEditItem(index, { quantity: e.target.value })}
                        disabled={menuEditSaving}
                      />
                    </label>
                    <button
                      type="button"
                      className="menu-edit-remove-btn"
                      onClick={() => removeMenuEditItem(index)}
                      disabled={menuEditSaving || menuEditDraft.items.length <= 1}
                    >
                      삭제
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="menu-edit-add-btn"
                onClick={addMenuEditItem}
                disabled={menuEditSaving}
              >
                + 상품 추가
              </button>
              <div className="add-customer-grid menu-edit-reason-grid">
                <label className="wide">
                  <span>수정 사유</span>
                  <input
                    value={menuEditDraft.reason}
                    onChange={(e) => setMenuEditDraft(prev => prev ? { ...prev, reason: e.target.value } : prev)}
                    placeholder="예: 간편식 1개 취소, 한식 1인으로 변경"
                    maxLength={300}
                    disabled={menuEditSaving}
                  />
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setMenuEditDraft(null)}
                disabled={menuEditSaving}
              >
                취소
              </button>
              <button
                className="submit-btn"
                onClick={saveMenuReplacement}
                disabled={menuEditSaving}
              >
                {menuEditSaving ? '저장 중...' : '상품 수정 저장'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isAdmin && showAddCustomerModal && (
        <div className="modal-overlay" onClick={() => !addCustomerSaving && setShowAddCustomerModal(false)}>
          <div className="modal-content add-customer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>고객추가</h3>
              <button
                className="close-btn"
                onClick={() => setShowAddCustomerModal(false)}
                disabled={addCustomerSaving}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="add-customer-grid">
                <label>
                  <span>동</span>
                  <input
                    value={addCustomerDraft.dong}
                    onChange={(e) => updateAddCustomerDraft({ dong: e.target.value })}
                    placeholder="예: 101"
                    disabled={addCustomerSaving}
                  />
                </label>
                <label>
                  <span>호</span>
                  <input
                    value={addCustomerDraft.ho}
                    onChange={(e) => updateAddCustomerDraft({ ho: e.target.value })}
                    placeholder="예: 1604"
                    disabled={addCustomerSaving}
                  />
                </label>
                <label className="wide">
                  <span>고객명</span>
                  <input
                    value={addCustomerDraft.customerName}
                    onChange={(e) => updateAddCustomerDraft({ customerName: e.target.value })}
                    placeholder="고객명"
                    disabled={addCustomerSaving}
                  />
                </label>
                <label>
                  <span>메뉴</span>
                  <select
                    value={addCustomerDraft.menu}
                    onChange={(e) => updateAddCustomerDraft({ menu: e.target.value })}
                    disabled={addCustomerSaving}
                  >
                    {addCustomerMenuOptions.map(menu => (
                      <option key={menu} value={menu}>{menu}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>수량</span>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={addCustomerDraft.quantity}
                    onChange={(e) => updateAddCustomerDraft({ quantity: e.target.value })}
                    disabled={addCustomerSaving}
                  />
                </label>
                <label className="add-customer-check wide">
                  <input
                    type="checkbox"
                    checked={addCustomerDraft.isCoolerBag}
                    onChange={(e) => updateAddCustomerDraft({ isCoolerBag: e.target.checked })}
                    disabled={addCustomerSaving}
                  />
                  <span>보냉가방 표시</span>
                </label>
                <label className="wide">
                  <span>메모</span>
                  <textarea
                    value={addCustomerDraft.note}
                    onChange={(e) => updateAddCustomerDraft({ note: e.target.value })}
                    placeholder="배송 시 볼 메모"
                    maxLength={500}
                    disabled={addCustomerSaving}
                  />
                </label>
                <label className="wide">
                  <span>추가 사유</span>
                  <input
                    value={addCustomerDraft.reason}
                    onChange={(e) => updateAddCustomerDraft({ reason: e.target.value })}
                    placeholder="예: 추가 주문, 누락 보정"
                    maxLength={300}
                    disabled={addCustomerSaving}
                  />
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setShowAddCustomerModal(false)}
                disabled={addCustomerSaving}
              >
                취소
              </button>
              <button
                className="submit-btn"
                onClick={addChartCustomer}
                disabled={addCustomerSaving}
              >
                {addCustomerSaving ? '추가 중...' : '고객 추가'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 남은 배송 개수 표시 */}
      {selectedDong !== '전체' && (() => {
        // 남은 배송: 미완료 주문을 동/호수로 그룹화하여 집 개수 계산
        const incompleteOrders = displayOrders.filter(
          o => !completedDeliveries.has(`${o.dong}-${o.ho}-${o.customer_name}`)
        );
        const uniqueHouses = new Set(incompleteOrders.map(o => `${o.dong}-${o.ho}`));
        const remainingHouses = uniqueHouses.size;
        
        // 동 완료 여부
        const isDongCompleted = remainingHouses === 0;
        
        // 다음 동 찾기
        const currentIndex = dongs.indexOf(selectedDong);
        const nextDong = currentIndex < dongs.length - 1 ? dongs[currentIndex + 1] : null;
        
        // 메뉴별 남은 개수 계산
        const remainingMenuCounts: { [key: string]: number } = {};
        incompleteOrders.forEach(o => {
          remainingMenuCounts[o.menu] = (remainingMenuCounts[o.menu] || 0) + 1;
        });
        
        // 메뉴 정렬
        const menuOrder = ['한식 1인', '한식1인', '한식 2인', '한식2인', '간편식', '샐러드', '샌드위치'];
        const sortedMenus = Object.entries(remainingMenuCounts).sort((a, b) => {
          const indexA = menuOrder.findIndex(m => a[0].includes(m.split(' ')[0]));
          const indexB = menuOrder.findIndex(m => b[0].includes(m.split(' ')[0]));
          if (indexA !== -1 && indexB !== -1) return indexA - indexB;
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;
          return a[0].localeCompare(b[0]);
        });
        
        if (isDongCompleted) {
          // 동 완료 시 다음 동 이동 버튼
          return (
            <div className="dong-completed-actions">
              <button 
                className="action-btn scroll-top"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              >
                최상단으로
              </button>
              {nextDong ? (
                <button 
                  className="action-btn next-dong"
                  onClick={() => {
                    setSelectedDong(nextDong);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  다음 동으로 ({nextDong}동)
                </button>
              ) : (
                <button 
                  className="action-btn go-home"
                  onClick={() => navigate('/dashboard')}
                >
                  메인화면으로
                </button>
              )}
            </div>
          );
        }
        
        return (
          <>
            <div 
              className={`remaining-count ${showRemainingDetail ? 'expanded' : ''}`}
              onClick={() => setShowRemainingDetail(!showRemainingDetail)}
            >
              <div className="remaining-summary">
                {selectedDong}동 남은 배송: <strong>{remainingHouses}건</strong>
              </div>
              
              {showRemainingDetail && (
                <div className="remaining-detail">
                  <div className="detail-title">남은 배송 상세:</div>
                  <div className="menu-detail-grid">
                    {sortedMenus.map(([menu, count]) => (
                      <div key={menu} className="menu-detail-item">
                        <span className="menu-detail-name">{menu}</span>
                        <span className="menu-detail-count">{count}개</span>
                      </div>
                    ))}
                  </div>
                  <div className="detail-total">
                    총 {incompleteOrders.length}개 메뉴 / {remainingHouses}건
                  </div>
                </div>
              )}
            </div>
          </>
        );
      })()}
      {/* 숨겨진 카메라 input */}
      <input
        type="file"
        accept="image/*"
        capture="environment"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handlePhotoSelected}
      />
      
      {/* 사진 확대 모달 */}
      {viewingPhoto && (
        <div className="photo-modal-overlay" onClick={() => setViewingPhoto(null)}>
          <div className="photo-modal" onClick={(e) => e.stopPropagation()}>
            <img src={viewingPhoto} alt="배송 증거 사진" />
            <button className="photo-modal-close" onClick={() => setViewingPhoto(null)}>✕</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveryChart;
